import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { JOURS, parseJours, trierJours } from "utils/planning";
import { useTranslation } from "react-i18next";
import { libelleMatiere } from "utils/valeurs";

const MATIERES = [
  { value: "quoran", label: "Qour'an" },
  { value: "arabe", label: "Langue arabe" },
  { value: "science", label: "Sciences de la religion" },
];

const MATIERES_COLORS = {
  quoran: { color: "#2e7d32", bg: "#e8f5e9" },
  arabe: { color: "#1565c0", bg: "#e3f2fd" },
  science: { color: "#e65100", bg: "#fff3e0" },
};

const fieldStyle = {
  width: "100%", height: "44px", padding: "0 14px",
  borderRadius: "8px", border: "1px solid #d2d6da",
  fontSize: "14px", color: "#344767", outline: "none",
  background: "#fff", boxSizing: "border-box",
  transition: "border-color 0.2s", fontFamily: "inherit",
};

function Section({ title, icon, children }) {
  return (
    <MDBox mb={4}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 32, height: 32, borderRadius: "8px",
          background: "linear-gradient(135deg, #2e7d32, #388e3c)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 16 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767" }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 3 }} />
      {children}
    </MDBox>
  );
}

function FieldLabel({ label, required, children }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="caption" fontWeight="bold" color="text" sx={{
        mb: 0.5, display: "block", textTransform: "uppercase",
        fontSize: 11, letterSpacing: "0.05em",
      }}>
        {label}{required && " *"}
      </MDTypography>
      {children}
    </MDBox>
  );
}

// Composant autocomplétion
function AutocompleteField({ label, required, items, value, onSelect, placeholder, renderItem }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);

  // Afficher le nom de l'élément sélectionné
  const selectedItem = items.find((i) => i.id === parseInt(value));

  useEffect(() => {
    if (selectedItem) setQuery(`${selectedItem.prenom} ${selectedItem.nom}`);
    else setQuery("");
  }, [value, items]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = query.length >= 1
    ? items.filter((i) =>
        `${i.prenom} ${i.nom} ${i.ville || ""}`.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : items.slice(0, 8);

  return (
    <FieldLabel label={label} required={required}>
      <MDBox ref={ref} sx={{ position: "relative" }}>
        <MDBox sx={{ position: "relative" }}>
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); onSelect(""); }}
            onFocus={() => { setFocused(true); setOpen(true); }}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            autoComplete="off"
            style={{
              ...fieldStyle,
              borderColor: focused ? "#2e7d32" : value ? "#2e7d32" : "#d2d6da",
              paddingRight: "40px",
            }}
          />
          {value ? (
            <MDBox onClick={() => { onSelect(""); setQuery(""); setOpen(false); }}
              sx={{
                position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                cursor: "pointer", color: "#9e9e9e",
                "&:hover": { color: "#c62828" },
              }}>
              <Icon sx={{ fontSize: 18 }}>close</Icon>
            </MDBox>
          ) : (
            <MDBox sx={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              color: "#9e9e9e", pointerEvents: "none",
            }}>
              <Icon sx={{ fontSize: 18 }}>search</Icon>
            </MDBox>
          )}
        </MDBox>

        {/* Dropdown résultats */}
        {open && filtered.length > 0 && (
          <MDBox sx={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000,
            background: "#fff", borderRadius: "10px", mt: 0.5,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            border: "1px solid #e3f2fd", overflow: "hidden",
            maxHeight: 280, overflowY: "auto",
          }}>
            {filtered.map((item) => (
              <MDBox key={item.id}
                onMouseDown={() => { onSelect(item.id); setQuery(`${item.prenom} ${item.nom}`); setOpen(false); }}
                sx={{
                  padding: "10px 14px", cursor: "pointer",
                  borderBottom: "1px solid #f5f5f5",
                  background: parseInt(value) === item.id ? "#e8f5e9" : "#fff",
                  "&:hover": { background: "#f0f7f0" },
                  "&:last-child": { borderBottom: "none" },
                  display: "flex", alignItems: "center", gap: 1.5,
                }}>
                {renderItem ? renderItem(item) : (
                  <>
                    <MDBox sx={{
                      width: 34, height: 34, borderRadius: "50%",
                      background: "#1565c0", color: "#fff",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700, flexShrink: 0, overflow: "hidden",
                    }}>
                      {item.photo ? (
                        <img src={item.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        `${item.prenom?.[0] || ""}${item.nom?.[0] || ""}`
                      )}
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767", display: "block" }}>
                        {item.prenom} {item.nom}
                      </MDTypography>
                      {item.ville && (
                        <MDTypography variant="caption" color="text">
                          📍 {item.ville}
                        </MDTypography>
                      )}
                    </MDBox>
                    {parseInt(value) === item.id && (
                      <Icon sx={{ color: "#2e7d32", fontSize: 18, ml: "auto" }}>check_circle</Icon>
                    )}
                  </>
                )}
              </MDBox>
            ))}
          </MDBox>
        )}

        {/* Aucun résultat */}
        {open && query.length >= 2 && filtered.length === 0 && (
          <MDBox sx={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1000,
            background: "#fff", borderRadius: "10px", mt: 0.5,
            boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
            border: "1px solid #e3f2fd", padding: "16px",
            textAlign: "center",
          }}>
            <MDTypography variant="caption" color="text">{t("cours.form.aucunResultat", { query })}</MDTypography>
          </MDBox>
        )}
      </MDBox>
    </FieldLabel>
  );
}

function SelectField({ label, name, value, onChange, options, required }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label} required={required}>
      <select name={name} value={value || ""} onChange={onChange}
        style={{ ...fieldStyle, color: value ? "#344767" : "#9ca3af" }}
        onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      >
        <option value="">{t("commun.choisir")}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </FieldLabel>
  );
}

function InputField({ label, name, value, onChange, type = "text", placeholder, multiline, rows, min }) {
  if (multiline) return (
    <FieldLabel label={label}>
      <textarea name={name} value={value || ""} onChange={onChange}
        placeholder={placeholder} rows={rows || 3} autoComplete="off"
        style={{ ...fieldStyle, height: "auto", padding: "10px 14px", resize: "vertical" }}
        onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
  return (
    <FieldLabel label={label}>
      <input name={name} type={type} value={value ?? ""} onChange={onChange} min={min}
        placeholder={placeholder} autoComplete="off" style={fieldStyle}
        onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
        onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
      />
    </FieldLabel>
  );
}

function ToggleField({ label, name, value, onChange }) {
  const { t } = useTranslation();
  return (
    <FieldLabel label={label}>
      <MDBox display="flex" alignItems="center" gap={1} sx={{
        height: "44px", background: "#f8fff8",
        borderRadius: "8px", border: "1px solid #d2d6da", padding: "0 14px",
      }}>
        {[{ label: t("commun.oui"), val: true }, { label: t("commun.non"), val: false }].map((opt) => (
          <MDBox key={opt.label}
            onClick={() => onChange({ target: { name, value: opt.val } })}
            sx={{
              padding: "4px 18px", borderRadius: "20px", border: "1px solid",
              borderColor: value === opt.val ? "#2e7d32" : "#d2d6da",
              background: value === opt.val ? "#2e7d32" : "#fff",
              color: value === opt.val ? "#fff" : "#344767",
              cursor: "pointer", fontSize: "13px", fontWeight: 500,
              transition: "all 0.2s",
            }}>
            {opt.label}
          </MDBox>
        ))}
      </MDBox>
    </FieldLabel>
  );
}

// Sélection multi-enseignants : menu déroulant à cases + un principal (radio) + notifications par enseignant
function CoEnseignantsField({ enseignants, value, onChange, readOnly = false }) {
  const { t } = useTranslation();
  const nomDe = (userId) => {
    const e = enseignants.find((x) => x.id === userId);
    return e ? `${e.prenom} ${e.nom}` : t("cours.form.enseignantNum", { id: userId });
  };

  // Garantit exactement un principal (le premier de la liste si le principal a été retiré)
  const avecPrincipal = (liste) => (liste.length > 0 && !liste.some((ce) => ce.est_principal)
    ? liste.map((ce, i) => ({ ...ce, est_principal: i === 0 }))
    : liste);

  const handleSelection = (e) => {
    const ids = typeof e.target.value === "string" ? e.target.value.split(",").map(Number) : e.target.value;
    onChange(avecPrincipal(ids.map((id) => value.find((ce) => ce.user_id === id)
      || { user_id: id, est_principal: false, recoit_notifications: true })));
  };
  const handleRemove = (userId) => onChange(avecPrincipal(value.filter((ce) => ce.user_id !== userId)));
  const handlePrincipalChange = (userId) =>
    onChange(value.map((ce) => ({ ...ce, est_principal: ce.user_id === userId })));
  const handleNotifChange = (userId) =>
    onChange(value.map((ce) => (ce.user_id === userId
      ? { ...ce, recoit_notifications: !ce.recoit_notifications } : ce)));

  return (
    <FieldLabel label={t("champs.enseignants")} required={!readOnly}>
      {!readOnly && (
        <Select
          multiple
          fullWidth
          displayEmpty
          value={value.map((ce) => ce.user_id)}
          onChange={handleSelection}
          renderValue={(ids) => (ids.length === 0
            ? <span style={{ color: "#9e9e9e" }}>{t("cours.form.choisirEnseignants")}</span>
            : t("cours.form.enseignantsSelectionnes", { count: ids.length }))}
          MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
          IconComponent={() => <Icon sx={{ mr: 1, color: "#9e9e9e", pointerEvents: "none" }}>expand_more</Icon>}
          sx={{
            minHeight: 44, borderRadius: "8px", background: "#fff", color: "#344767",
            "& .MuiSelect-select": { padding: "10px 14px !important", fontSize: 14 },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d2d6da" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2e7d32" },
          }}
        >
          {enseignants.map((e) => (
            <MenuItem key={e.id} value={e.id} dense>
              <Checkbox size="small" checked={value.some((ce) => ce.user_id === e.id)} />
              <ListItemText primary={`${e.prenom} ${e.nom}`} primaryTypographyProps={{ fontSize: 14 }} />
            </MenuItem>
          ))}
        </Select>
      )}
      {value.length > 0 && (
        <MDBox mt={readOnly ? 0 : 1} sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
          {value.map((ce, i) => (
            <MDBox key={ce.user_id} display="flex" alignItems="center" flexWrap="wrap" gap={1}
              sx={{ px: 1.5, py: readOnly ? 1 : 0.5, borderTop: i ? "1px solid #f0f0f0" : "none",
                background: ce.est_principal ? "#f1f8e9" : "#fff" }}>
              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767", flex: 1, minWidth: 140 }}>
                {nomDe(ce.user_id)}
              </MDTypography>
              {readOnly ? (
                <>
                  {ce.est_principal && <MDTypography variant="caption" color="success" fontWeight="bold">{t("cours.form.principal")}</MDTypography>}
                  {ce.recoit_notifications && <Icon sx={{ fontSize: 16, color: "#9e9e9e" }} titleAccess={t("cours.form.recoitNotifs")}>notifications</Icon>}
                </>
              ) : (
                <>
                  <FormControlLabel
                    control={<Radio size="small" checked={ce.est_principal} onChange={() => handlePrincipalChange(ce.user_id)} />}
                    label={<MDTypography variant="caption">{t("cours.form.principal")}</MDTypography>}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={ce.recoit_notifications} onChange={() => handleNotifChange(ce.user_id)} />}
                    label={<MDTypography variant="caption">{t("cours.form.recoitNotifs")}</MDTypography>}
                    sx={{ m: 0 }}
                  />
                  <IconButton size="small" onClick={() => handleRemove(ce.user_id)} title={t("cours.form.retirer")}
                    sx={{ color: "#9e9e9e", "&:hover": { color: "#c62828" } }}>
                    <Icon sx={{ fontSize: 18 }}>close</Icon>
                  </IconButton>
                </>
              )}
            </MDBox>
          ))}
        </MDBox>
      )}
    </FieldLabel>
  );
}

function CoursForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin, user } = useAuth();
  const isEdit = Boolean(id && id !== "nouveau");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [eleves, setEleves] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [livres, setLivres] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [livresSelectionnes, setLivresSelectionnes] = useState([]);

  const [mode, setMode] = useState("individuel");

  const [form, setForm] = useState({
    eleve_id: searchParams.get("eleve") || "",
    groupe_id: searchParams.get("groupe") || "",
    enseignant_id: isAdmin ? "" : user?.id || "",
    coEnseignants: [],
    matiere: "",
    chapitres_ids: [],
    lieu: "",
    jours_semaine: [],
    heure_cours: "",
    chapitre_termine: false,
    examen: "",
    nb_participants: "",
    commentaire_general: "",
    date_conference: "",
  });

  useEffect(() => {
    if (searchParams.get("groupe")) setMode("groupe");

    Promise.all([
      api.get("/eleves"),
      api.get("/enseignants"),
      api.get("/livres"),
      api.get("/groupes"),
    ]).then(([e, ens, l, g]) => {
      setEleves(e.data);
      setEnseignants(ens.data);
      setLivres(l.data);
      setGroupes(g.data);
    });

    if (isEdit) {
      api.get(`/cours/${id}`).then(({ data }) => {
        if (data.type === "general") setMode("general");
        else if (data.groupe_id) setMode("groupe");
        setForm({
          eleve_id: data.eleve_id || "",
          groupe_id: data.groupe_id || "",
          enseignant_id: data.enseignant_id || "",
          coEnseignants: (data.coEnseignants?.length
            ? [...data.coEnseignants].sort((a, b) =>
              Number(b.CoursEnseignant?.est_principal) - Number(a.CoursEnseignant?.est_principal))
              .map((e) => ({
                user_id: e.id,
                est_principal: Boolean(e.CoursEnseignant?.est_principal),
                recoit_notifications: e.CoursEnseignant?.recoit_notifications !== false,
              }))
            : data.enseignant_id
              ? [{ user_id: data.enseignant_id, est_principal: true, recoit_notifications: true }]
              : []),
          matiere: data.matiere || "",
          chapitres_ids: data.chapitres?.map((c) => c.id) || [],
          lieu: data.lieu || "",
          jours_semaine: parseJours(data.jours_semaine),
          heure_cours: data.heure_cours ? data.heure_cours.slice(0, 5) : "",
          chapitre_termine: data.chapitre_termine || false,
          examen: data.examen || "",
          nb_participants: data.nb_participants ?? "",
          commentaire_general: data.commentaire_general || "",
          date_conference: data.date_conference || "",
        });

        if (data.chapitres && data.chapitres.length > 0) {
          const livreIds = [...new Set(data.chapitres.map((c) => c.livre_id).filter(Boolean))];
          Promise.all(livreIds.map((lid) => api.get(`/livres/${lid}/chapitres`)))
            .then((results) => {
              setLivresSelectionnes(livreIds.map((lid, i) => ({
                livre_id: lid,
                chapitresDispos: results[i].data,
                chapitres: data.chapitres.filter((c) => c.livre_id === lid).map((c) => c.id),
              })));
            });
        }
      });
    }
  }, [id, isEdit]);

  const addLivre = async (livreId) => {
    if (!livreId) return;
    if (livresSelectionnes.find((l) => l.livre_id === parseInt(livreId))) return;
    const { data } = await api.get(`/livres/${livreId}/chapitres`);
    setLivresSelectionnes((prev) => [...prev, {
      livre_id: parseInt(livreId),
      chapitresDispos: data,
      chapitres: [],
    }]);
  };

  const removeLivre = (livreId) => {
    const livre = livresSelectionnes.find((l) => l.livre_id === livreId);
    if (livre) {
      setForm((prev) => ({
        ...prev,
        chapitres_ids: prev.chapitres_ids.filter((cid) => !livre.chapitres.includes(cid)),
      }));
    }
    setLivresSelectionnes((prev) => prev.filter((l) => l.livre_id !== livreId));
  };

  const toggleChapitre = (chapId, livreId) => {
    const current = form.chapitres_ids || [];
    const updated = current.includes(chapId)
      ? current.filter((x) => x !== chapId)
      : [...current, chapId];
    setForm((prev) => ({ ...prev, chapitres_ids: updated }));
    setLivresSelectionnes((prev) =>
      prev.map((l) => {
        if (l.livre_id !== livreId) return l;
        const chapitres = l.chapitres.includes(chapId)
          ? l.chapitres.filter((c) => c !== chapId)
          : [...l.chapitres, chapId];
        return { ...l, chapitres };
      })
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleJour = (jour) => {
    setForm((prev) => ({
      ...prev,
      jours_semaine: prev.jours_semaine.includes(jour)
        ? prev.jours_semaine.filter((j) => j !== jour)
        : [...prev.jours_semaine, jour],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "individuel" && !form.eleve_id) {
      setError(t("cours.form.erreurs.eleve"));
      return;
    }
    if (mode === "groupe" && !form.groupe_id) {
      setError(t("cours.form.erreurs.groupe"));
      return;
    }
    if (mode === "general" && form.nb_participants !== "" && Number(form.nb_participants) < 0) {
      setError(t("cours.form.erreurs.participants"));
      return;
    }
    if (mode !== "general" && !form.matiere) {
      setError(t("cours.form.erreurs.matiere"));
      return;
    }
    if (isAdmin && form.coEnseignants.length === 0) {
      setError(t("cours.form.erreurs.enseignant"));
      return;
    }
    // Conférence datée (cours général) : pas de récurrence, les jours sont facultatifs
    const estDate = mode === "general" && Boolean(form.date_conference);
    if (!estDate && form.jours_semaine.length === 0) {
      setError(t("cours.form.erreurs.jour"));
      return;
    }
    if (!form.heure_cours) {
      setError(t("cours.form.erreurs.heure"));
      return;
    }

    setError("");
    setSaving(true);

    const cleanForm = {
      type: mode,
      eleve_id: mode === "individuel" ? form.eleve_id : null,
      groupe_id: mode === "groupe" ? form.groupe_id : null,
      // Admin : équipe enseignante complète ; enseignant : le backend gère (principal à la création, inchangé en édition)
      ...(isAdmin
        ? { coEnseignants: form.coEnseignants }
        : isEdit ? {} : { enseignant_id: user.id }),
      matiere: mode === "general" ? null : form.matiere,
      chapitres_ids: mode === "general" ? [] : form.chapitres_ids || [],
      lieu: form.lieu || null,
      jours_semaine: trierJours(form.jours_semaine),
      heure_cours: form.heure_cours,
      chapitre_termine: form.chapitre_termine,
      examen: mode === "general" ? null : form.examen || null,
      nb_participants: mode === "general" && form.nb_participants !== "" ? parseInt(form.nb_participants, 10) : null,
      commentaire_general: mode === "general" ? form.commentaire_general || null : null,
      date_conference: mode === "general" ? form.date_conference || null : null,
    };

    try {
      if (isEdit) {
        await api.put(`/cours/${id}`, cleanForm);
      } else {
        await api.post("/cours", cleanForm);
      }
      navigate("/cours");
    } catch (err) {
      setError(err.response?.data?.message || t("commun.erreurEnregistrement"));
    } finally {
      setSaving(false);
    }
  };

  const livresDisponibles = livres.filter(
    (l) =>
      !livresSelectionnes.find((ls) => ls.livre_id === l.id) &&
      (!form.matiere || l.matiere === form.matiere)
  );

  const groupeSelectionne = groupes.find((g) => g.id === parseInt(form.groupe_id));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>

            <MDBox mb={3} display="flex" alignItems="center" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/cours")}
                sx={{ borderRadius: "10px", px: 2, whiteSpace: "nowrap" }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon> {t("commun.retourListe")}
              </MDButton>
              <MDTypography variant="h5" fontWeight="bold" sx={{ color: "#344767" }}>
                {isEdit ? t("cours.form.titreModifier") : t("cours.planifier")}
              </MDTypography>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDBox mx={2} mt={-3} py={2} px={3} sx={{
                background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(46,125,50,0.4)",
              }}>
                <MDTypography variant="h6" color="white" fontWeight="bold">
                  {t("cours.form.bandeau")}
                </MDTypography>
              </MDBox>

              <MDBox pt={4} pb={4} px={4}>
                {error && (
                  <MDBox mb={3} p={2} sx={{
                    background: "#ffebee", borderRadius: "10px", border: "1px solid #ffcdd2",
                  }}>
                    <MDTypography variant="caption" color="error" fontWeight="medium">
                      ⚠️ {error}
                    </MDTypography>
                  </MDBox>
                )}

                <MDBox component="form" onSubmit={handleSubmit} autoComplete="off">

                  {/* ── TYPE DE COURS ── */}
                  <MDBox mb={4}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{
                      textTransform: "uppercase", fontSize: 11,
                      letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1.5,
                    }}>
                      {t("cours.form.typeCours")}
                    </MDTypography>
                    <MDBox display="flex" gap={2} flexWrap="wrap">
                      {[
                        { val: "individuel", label: t("cours.form.types.individuel"), icon: "person", desc: t("cours.form.types.individuelDesc") },
                        { val: "groupe", label: t("cours.form.types.groupe"), icon: "groups", desc: t("cours.form.types.groupeDesc") },
                        { val: "general", label: t("valeurs.typeCours.general"), icon: "campaign", desc: t("cours.form.types.generalDesc") },
                      ].map((opt) => (
                        <MDBox key={opt.val}
                          onClick={() => !isEdit && setMode(opt.val)}
                          sx={{
                            flex: 1, p: 2, borderRadius: "12px", cursor: isEdit ? "default" : "pointer",
                            border: "2px solid",
                            borderColor: mode === opt.val ? "#2e7d32" : "#d2d6da",
                            background: mode === opt.val ? "#e8f5e9" : "#fff",
                            transition: "all 0.2s",
                            "&:hover": !isEdit ? { borderColor: "#2e7d32", background: "#f1f8f1" } : {},
                          }}>
                          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Icon sx={{
                              fontSize: 20,
                              color: mode === opt.val ? "#2e7d32" : "#9e9e9e",
                            }}>
                              {opt.icon}
                            </Icon>
                            <MDTypography variant="button" fontWeight="bold" sx={{
                              color: mode === opt.val ? "#2e7d32" : "#344767",
                            }}>
                              {opt.label}
                            </MDTypography>
                            {mode === opt.val && (
                              <Icon sx={{ color: "#2e7d32", fontSize: 18, ml: "auto" }}>check_circle</Icon>
                            )}
                          </MDBox>
                          <MDTypography variant="caption" color="text">
                            {opt.desc}
                          </MDTypography>
                        </MDBox>
                      ))}
                    </MDBox>
                  </MDBox>

                  {/* ── SECTION 1 : PARTICIPANTS ── */}
                  <Section title={t("champs.participants")} icon="people">
                    <Grid container spacing={2}>

                      {/* Mode individuel — Autocomplétion élève */}
                      {mode === "individuel" && (
                        <Grid item xs={12} md={6}>
                          <AutocompleteField
                            label={t("champs.eleve")} required
                            items={eleves}
                            value={form.eleve_id}
                            onSelect={(val) => setForm((p) => ({ ...p, eleve_id: val }))}
                            placeholder={t("cours.form.rechercherEleve")}
                          />
                        </Grid>
                      )}

                      {/* Mode groupe */}
                      {mode === "groupe" && (
                        <Grid item xs={12} md={6}>
                          <SelectField
                            label={`${t("champs.groupe")} *`} name="groupe_id"
                            value={form.groupe_id} onChange={handleChange} required
                            options={groupes.map((g) => ({
                              value: g.id,
                              label: `${g.nom} (${t("cours.nbEleves", { count: g.eleves?.length || 0 })})`,
                            }))}
                          />
                        </Grid>
                      )}

                      {/* Enseignants (co-enseignants) */}
                      {(isAdmin || form.coEnseignants.length > 0) && (
                        <Grid item xs={12}>
                          <CoEnseignantsField
                            enseignants={enseignants}
                            value={form.coEnseignants}
                            readOnly={!isAdmin}
                            onChange={(liste) => setForm((p) => ({ ...p, coEnseignants: liste }))}
                          />
                        </Grid>
                      )}

                      {/* Mode général / conférence */}
                      {mode === "general" && (
                        <>
                          <Grid item xs={12} md={6}>
                            <InputField
                              label={t("cours.form.nbParticipants")} name="nb_participants" type="number" min={0}
                              value={form.nb_participants} onChange={handleChange} placeholder={t("cours.form.nbParticipantsPlaceholder")}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <InputField
                              label={t("champs.commentaire")} name="commentaire_general" multiline rows={3}
                              value={form.commentaire_general} onChange={handleChange}
                              placeholder={t("cours.form.commentairePlaceholder")}
                            />
                          </Grid>
                        </>
                      )}

                      {/* Aperçu groupe sélectionné */}
                      {mode === "groupe" && groupeSelectionne && (
                        <Grid item xs={12}>
                          <MDBox p={2} sx={{
                            background: "#e8f5e9", borderRadius: "12px",
                            border: "1px solid #a5d6a7",
                          }}>
                            <MDTypography variant="caption" fontWeight="bold" sx={{
                              color: "#2e7d32", textTransform: "uppercase",
                              fontSize: 10, letterSpacing: "0.05em", display: "block", mb: 1,
                            }}>
                              👥 {t("cours.form.elevesGroupe")}
                            </MDTypography>
                            <MDBox display="flex" flexWrap="wrap" gap={1}>
                              {(groupeSelectionne.eleves || []).map((eleve) => (
                                <MDBox key={eleve.id} sx={{
                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                  padding: "3px 10px", borderRadius: "20px",
                                  background: "#2e7d32", color: "#fff",
                                  fontSize: 12, fontWeight: 600,
                                }}>
                                  {eleve.prenom} {eleve.nom}
                                </MDBox>
                              ))}
                            </MDBox>
                            <MDTypography variant="caption" color="text" sx={{ mt: 1, display: "block" }}>
                              ℹ️ {t("cours.form.suiviIndependant")}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>
                  </Section>

                  {/* ── SECTION 2 : MATIÈRE & LIVRES (pas pour les cours généraux) ── */}
                  {mode !== "general" && (
                  <Section title={t("cours.form.matiereLivres")} icon="school">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <SelectField
                          label={`${t("champs.matiere")} *`} name="matiere"
                          value={form.matiere} onChange={handleChange} required
                          options={MATIERES.map((m) => ({ ...m, label: libelleMatiere(m.value) }))}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FieldLabel label={t("cours.form.ajouterLivre")}>
                          <MDBox display="flex" gap={1}>
                            <select id="livre-select" defaultValue=""
                              style={{ ...fieldStyle, flex: 1, color: "#9ca3af" }}
                              onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
                              onBlur={(e) => (e.target.style.borderColor = "#d2d6da")}
                            >
                              <option value="">{t("cours.form.choisirLivre")}</option>
                              {livresDisponibles.map((l) => (
                                <option key={l.id} value={l.id}>{l.nom}</option>
                              ))}
                            </select>
                            <MDBox onClick={() => {
                              const sel = document.getElementById("livre-select");
                              if (sel.value) { addLivre(sel.value); sel.value = ""; }
                            }} sx={{
                              height: 44, px: 2.5, borderRadius: "8px",
                              background: "#2e7d32", color: "#fff",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 13, fontWeight: 700, gap: 0.5,
                              whiteSpace: "nowrap", flexShrink: 0,
                              "&:hover": { background: "#1b5e20" },
                            }}>
                              <Icon sx={{ fontSize: 18 }}>add</Icon>
                              {t("commun.ajouter")}
                            </MDBox>
                          </MDBox>
                        </FieldLabel>
                      </Grid>

                      {livresSelectionnes.length > 0 && (
                        <Grid item xs={12}>
                          {livresSelectionnes.map((ls) => {
                            const livre = livres.find((l) => l.id === ls.livre_id);
                            const m = MATIERES_COLORS[form.matiere] || { color: "#2e7d32", bg: "#e8f5e9" };
                            return (
                              <MDBox key={ls.livre_id} mb={2} sx={{
                                background: m.bg, borderRadius: "12px",
                                border: `1px solid ${m.color}33`, overflow: "hidden",
                              }}>
                                <MDBox px={2} py={1.5} display="flex"
                                  justifyContent="space-between" alignItems="center"
                                  sx={{ background: `${m.color}15`, borderBottom: `1px solid ${m.color}22` }}>
                                  <MDBox display="flex" alignItems="center" gap={1}>
                                    <Icon sx={{ color: m.color, fontSize: 18 }}>menu_book</Icon>
                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: m.color }}>
                                      {livre?.nom || t("champs.livre")}
                                    </MDTypography>
                                    <MDBox sx={{
                                      background: m.color, color: "#fff",
                                      borderRadius: "10px", padding: "1px 8px",
                                      fontSize: 11, fontWeight: 600,
                                    }}>
                                      {t("cours.form.chapSelectionnes", { count: ls.chapitres.length })}
                                    </MDBox>
                                  </MDBox>
                                  <MDBox onClick={() => removeLivre(ls.livre_id)} sx={{
                                    width: 28, height: 28, borderRadius: "8px",
                                    background: "#ffebee", color: "#c62828",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>close</Icon>
                                  </MDBox>
                                </MDBox>
                                <MDBox px={2} py={1.5}>
                                  {ls.chapitresDispos.length === 0 ? (
                                    <MDTypography variant="caption" color="text">
                                      {t("suivi.aucunChapitre")}
                                    </MDTypography>
                                  ) : (
                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                      {ls.chapitresDispos.map((chap) => {
                                        const selected = (form.chapitres_ids || []).includes(chap.id);
                                        return (
                                          <MDBox key={chap.id}
                                            onClick={() => toggleChapitre(chap.id, ls.livre_id)}
                                            sx={{
                                              padding: "5px 14px", borderRadius: "20px",
                                              border: "1px solid",
                                              borderColor: selected ? m.color : "#d2d6da",
                                              background: selected ? m.color : "#fff",
                                              color: selected ? "#fff" : "#344767",
                                              cursor: "pointer", fontSize: "13px", fontWeight: 500,
                                              userSelect: "none", transition: "all 0.2s",
                                              display: "flex", alignItems: "center", gap: 0.5,
                                            }}>
                                            {selected && <Icon sx={{ fontSize: 13 }}>check</Icon>}
                                            {t("suivi.prefixeChapitre")} {chap.numero} — {chap.titre}
                                          </MDBox>
                                        );
                                      })}
                                    </MDBox>
                                  )}
                                </MDBox>
                              </MDBox>
                            );
                          })}
                          {form.chapitres_ids.length > 0 && (
                            <MDTypography variant="caption" sx={{ color: "#2e7d32", fontWeight: 600 }}>
                              ✓ {t("cours.form.totalChapitres", { count: form.chapitres_ids.length })}
                            </MDTypography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Section>
                  )}

                  {/* ── SECTION 3 : PLANIFICATION ── */}
                  <Section title={t("cours.form.planification")} icon="schedule">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <InputField label={t("champs.lieu")} name="lieu"
                          value={form.lieu} onChange={handleChange}
                          placeholder={t("cours.form.lieuPlaceholder")} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FieldLabel label={`${t("champs.heure")} *`}>
                          <input name="heure_cours" type="time"
                            value={form.heure_cours || ""} onChange={handleChange}
                            autoComplete="off" style={fieldStyle}
                            onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
                            onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                        </FieldLabel>
                      </Grid>
                      {mode === "general" && (
                        <Grid item xs={12} md={6}>
                          <FieldLabel label={t("cours.form.dateConference")}>
                            <input name="date_conference" type="date"
                              value={form.date_conference || ""} onChange={handleChange}
                              autoComplete="off" style={fieldStyle}
                              onFocus={(e) => (e.target.style.borderColor = "#2e7d32")}
                              onBlur={(e) => (e.target.style.borderColor = "#d2d6da")} />
                          </FieldLabel>
                          <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                            {t("cours.form.dateConferenceAide")}
                          </MDTypography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <FieldLabel label={t("champs.jours")} required={!(mode === "general" && form.date_conference)}>
                          <MDBox display="flex" flexWrap="wrap" gap={1}>
                            {JOURS.map((j) => {
                              const actif = form.jours_semaine.includes(j.value);
                              return (
                                <label key={j.value} style={{
                                  display: "flex", alignItems: "center", gap: "6px",
                                  padding: "8px 14px", borderRadius: "8px", cursor: "pointer",
                                  fontSize: "14px", userSelect: "none",
                                  border: `1px solid ${actif ? "#2e7d32" : "#d2d6da"}`,
                                  background: actif ? "#e8f5e9" : "#fff",
                                  color: actif ? "#2e7d32" : "#344767",
                                }}>
                                  <input type="checkbox" checked={actif}
                                    onChange={() => toggleJour(j.value)}
                                    style={{ accentColor: "#2e7d32", margin: 0 }} />
                                  {j.label}
                                </label>
                              );
                            })}
                          </MDBox>
                        </FieldLabel>
                      </Grid>
                    </Grid>
                  </Section>

                  {/* ── SECTION 4 : SUIVI (pas pour les cours généraux) ── */}
                  {mode !== "general" && (
                  <Section title={t("cours.form.suivi")} icon="fact_check">
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <ToggleField label={t("cours.form.livreTermine")}
                          name="chapitre_termine" value={form.chapitre_termine}
                          onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12}>
                        <InputField label={t("cours.form.notesExamen")}
                          name="examen" value={form.examen} onChange={handleChange}
                          multiline rows={3}
                          placeholder={t("cours.form.notesExamenPlaceholder")} />
                      </Grid>
                    </Grid>
                  </Section>
                  )}

                  {/* ── BOUTONS ── */}
                  <MDBox display="flex" gap={2} justifyContent="flex-end" mt={2}>
                    <MDButton variant="outlined" color="secondary"
                      onClick={() => navigate("/cours")}>
                      {t("commun.annuler")}
                    </MDButton>
                    <MDButton type="submit" disabled={saving} sx={{
                      px: 4, background: "linear-gradient(135deg, #2e7d32, #388e3c)",
                      color: "#fff", borderRadius: "8px", fontWeight: 700,
                      "&:hover": { background: "#1b5e20" },
                    }}>
                      {saving ? t("commun.enregistrement") : isEdit ? t("commun.mettreAJour") : t("cours.form.creer")}
                    </MDButton>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CoursForm;